import React from "react";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
} from "recharts";

import { useTranslation } from "lib/i18n/use-translation";

import { K, useDomainName } from "./parts";
import { DOMAINS, Domain, formatScore } from "./resilience-view";
import styles from "./resilience.module.scss";

export type RadarSeries = {
  name: string;
  // A domain missing here is hidden for this series.
  values: Partial<Record<Domain, number>>;
  tone: "accent" | "muted";
};

// Wide enough beside the radius for the longest translated domain name.
const WIDTH = 520;
const HEIGHT = 244;
const RADIUS = 100;

// Fewer points than this make a line or a dot, not a shape.
export const MINIMUM_DOMAINS = 3;

// The domains every series shows; a domain hidden in any is left off.
export const radarDomains = (series: RadarSeries[]) =>
  DOMAINS.filter((domain) =>
    series.every((one) => one.values[domain] !== undefined)
  );

/**
 * The domains every series shows, drawn on one radar. A domain hidden in any
 * series is left off rather than drawn as zero. The chart is drawn at a fixed
 * size and scales down with its card, until its labels would be too small to
 * read; past that the card scrolls sideways.
 */
const ScoreRadar = ({
  series,
  format = formatScore,
}: {
  series: RadarSeries[];
  // How a score is written in the radar's text description.
  format?: (value: number, locale: string) => string;
}) => {
  const { t, i18n } = useTranslation(["admin"]);
  const domainName = useDomainName();
  const domains = radarDomains(series);
  if (domains.length < MINIMUM_DOMAINS) return null;

  const data = domains.map((domain) => ({
    domain: domainName(domain),
    ...Object.fromEntries(
      series.map((one, index) => [`series${index}`, one.values[domain]])
    ),
  }));
  const description = series
    .map((one) =>
      t(`${K}.radar.series`, {
        name: one.name,
        scores: domains
          .map(
            (domain) =>
              `${domainName(domain)} ${format(
                one.values[domain] as number,
                i18n.language
              )}`
          )
          .join(", "),
      })
    )
    .join(" ");

  return (
    <figure className={styles["radar"]}>
      <div className={styles["radar-chart"]} aria-hidden="true">
        <RadarChart
          width={WIDTH}
          height={HEIGHT}
          data={data}
          outerRadius={RADIUS}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        >
          <PolarGrid />
          <PolarAngleAxis dataKey="domain" />
          <PolarRadiusAxis
            domain={[0, 100]}
            tick={false}
            axisLine={false}
            tickCount={5}
          />
          {series.map((one, index) => (
            <Radar
              key={index}
              name={one.name}
              dataKey={`series${index}`}
              className={styles[`radar-${one.tone}`]}
              isAnimationActive={false}
            />
          ))}
        </RadarChart>
      </div>
      <figcaption className={styles["visually-hidden"]}>
        {description}
      </figcaption>
    </figure>
  );
};

export default ScoreRadar;
